import { Component, OnInit } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Observable, Subscription } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import { EnodeService } from 'src/app/services/enode/enode.service';
import { App } from '@capacitor/app';
import {
  EnodeMe,
  EnodeVehicle,
  UserLocation,
  VehiclePayload,
  VendorActionPayload
} from 'src/app/services/enode/enode.types';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { FeatureTogglesService } from 'src/app/services/feature-toggles/feature-toggles.service';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { InformationService } from 'src/app/services/notify/information.service';
import { InformationMessage } from 'src/app/services/notify/information.types';
@Component({
  selector: 'app-enode',
  templateUrl: './enode.page.html',
  styleUrls: ['./enode.page.scss']
})
export class EnodePage implements OnInit {
  enodeMe: EnodeMe;
  hasLinkedVehicle: boolean;
  lastUpdated: string;
  chargeActionMessages: { [key: string]: string };
  hasFetchingErrorVehicles: boolean;
  hasFetchingErrorEnodeMe: boolean;

  isLoading: boolean = false;
  locations: UserLocation[];

  newVehicle: EnodeVehicle;
  doSetup: boolean = false;

  vehicles$: Observable<EnodeVehicle[]>;
  vehicles: EnodeVehicle[];
  selectedVehicleId: string;

  subscription: Subscription;
  vendorActionSocket: Observable<VendorActionPayload>;
  vehicleSocket: Observable<VehiclePayload>;
  vehicleDiscoveredSocket: Observable<VehiclePayload>;
  vehicleDeletedSocket: Observable<VehiclePayload>;

  showMenu: boolean = false;
  infoMessages: InformationMessage[] = [];

  constructor(
    private _enodeService: EnodeService,
    public modalCtrl: ModalController,
    public toggleService: FeatureTogglesService,
    public alertController: AlertController,
    public settingsService: SettingsService,
    public infoService: InformationService,
    public platform: Platform
  ) {}

  ngOnInit() {
    this.infoService.getInfoMessages().subscribe({
      next: (res) => {
        this.infoMessages = res.filter((x) => x.aktivert) || [];
      }
    });
    this.hasFetchingErrorVehicles = false;
    this.hasFetchingErrorEnodeMe = false;
    this.chargeActionMessages = {};
    this.lastUpdated = new Date().toLocaleDateString('nb-NO', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    });

    this.getMe();
    this.getVehicles();
  }

  checkMenuCustomization() {
    this.settingsService.showEnodeInMenu$.subscribe((showInMenu) => {
      if (!showInMenu) {
        Preferences.get({ key: 'hasReadEnodeMenuToggle' }).then(async (res) => {
          if (res.value !== 'true') {
            const alert = await this.alertController.create({
              cssClass: '',
              mode: 'ios',
              header: 'Tilpasning av meny',
              message: `<p>Hvis du bruker smartlading ofte kan det være kjekt å ha den lett tilgjengelig.</p>
                <p>Om du vil, kan vi legge smartlading inn i hovedmenyen så den bare er ett trykk unna. Avtaler vil da flyttes til sidemenyen. Du kan alltids endre dette senere i innstillinger under Profil.</p>
                <p class="m-0">Ønsker du å ha «Smartlading» i hovedmenyen?</p>`,
              buttons: [
                {
                  text: 'Nei, behold menyen som den er',
                  role: 'cancel'
                },
                {
                  text: 'Ja, bruk i hovedmeny',
                  role: 'confirm',
                  handler: () => {
                    this.settingsService.setShowEnodeInMenu(true);
                  }
                }
              ],
              backdropDismiss: false
            });
            await alert.present();
            Preferences.set({
              key: 'hasReadEnodeMenuToggle',
              value: 'true'
            });
          }
        });
      }
    });
  }

  ionViewWillEnter() {

    App.addListener('appStateChange', (data) => {
      if (data.isActive && this.platform.url().includes("enode")) {
        // Connects to sockets again if app has been in the background
        this._enodeService.connectSocket();
        this.isLoading = false;
        this.getMe();
      } else {
        // Disconnect websockets when phone goes in the background
        this._enodeService.disconnectSocket();
      }
    })
    this._enodeService.connectSocket();

    this.vendorActionSocket = this._enodeService.getVendorActionStream();
    this.vehicleSocket = this._enodeService.getVehicleStream();
    this.vehicleDiscoveredSocket =
      this._enodeService.getVehicleDiscoveredStream();
    this.vehicleDeletedSocket = this._enodeService.getVehicleDeletedStream();
    this.vehicleSocket.subscribe({
      next: (data) => {
        this.vehicles = this.vehicles.map((vehicle) =>
          vehicle.id === data.payload.id ? data.payload : vehicle
        );
        this.lastUpdated = new Date().toLocaleDateString('nb-NO', {
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        });
      }
    });

    this.vehicleDiscoveredSocket.subscribe({
      next: (data) => {
        this.showMenu = false;
        this.newVehicle = data.payload;
        if (data.payload?.id) {
          Preferences.set({
            key: 'selectedVehicleId',
            value: data.payload.id
          });
        }
        // Setup kjøres hvis første bil
        if (this.vehicles.length === 0) {
          this.doSetup = true;
        }
        this.getVehicles();
        this.hasLinkedVehicle = true;
        this.lastUpdated = new Date().toLocaleDateString('nb-NO', {
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        });
        this.isLoading = false;
      }
    });

    this.vendorActionSocket.subscribe((data) => {
      const vehicleId = data.payload.targetId;
      if (data.payload.kind === 'STOP') {
        switch (data.payload.state) {
          case 'CONFIRMED':
            this.chargeActionMessages[vehicleId] = 'Lading stoppet';
            break;
          case 'CANCELLED':
            this.chargeActionMessages[vehicleId] = 'Stopp av lading avbrutt';
            break;
          case 'PENDING':
            this.chargeActionMessages[vehicleId] =
              'Stopper lading... Vennligst vent';
            break;
          case 'FAILED':
            this.chargeActionMessages[vehicleId] = 'Stopp av lading feilet';
            break;
          default:
            this.chargeActionMessages[vehicleId] = '';
            break;
        }
      } else if (data.payload.kind === 'START') {
        switch (data.payload.state) {
          case 'CONFIRMED':
            this.chargeActionMessages[vehicleId] = 'Lading startet';
            break;
          case 'PENDING':
            this.chargeActionMessages[vehicleId] =
              'Lading starter... Vennligst vent';
            break;
          case 'FAILED':
            this.chargeActionMessages[vehicleId] = 'Lading feilet';
          case 'CANCELLED':
            this.chargeActionMessages[vehicleId] = 'Lading avbrutt';
            break;
          default:
            this.chargeActionMessages[vehicleId] = '';
            break;
        }
      }
    });

    this.vehicleDeletedSocket.subscribe({
      next: (data) => {
        this.vehicles = this.vehicles.filter((v) => v.id !== data.payload.id);
        if (this.selectedVehicleId === data.payload.id) {
          this.selectedVehicleId = this.vehicles[0]?.id;
        }
        this.lastUpdated = new Date().toLocaleDateString('nb-NO', {
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        });
      }
    });

    this._enodeService.reconnectSocket();
  }

  ionViewWillLeave() {
    Browser.removeAllListeners();
    this._enodeService.disconnectSocket();
  }

  getMe() {    
    this.isLoading = true;
    this.hasFetchingErrorEnodeMe = false;
    this._enodeService.getMe().subscribe({
      next: (enodeMe) => {
        this.enodeMe = enodeMe;
        this.hasLinkedVehicle = enodeMe?.vehicles?.length > 0;

        if (this.hasLinkedVehicle) {
          if (this.vehicles?.length !== enodeMe.vehicles?.length) {
            this.getVehicles();
          } else {
            this.isLoading = false;
          } 
          this.lastUpdated = new Date().toLocaleDateString('nb-NO', {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric'
          });
          this._enodeService.getLocations().subscribe({
            next: (locations) => {
              this.locations = locations;
            }
          });
          if (this.hasLinkedVehicle) {
            this.checkMenuCustomization();
          }
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.hasFetchingErrorEnodeMe = true;
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  async getVehicles() {
    this.isLoading = true;
    this.hasFetchingErrorVehicles = false;
    this.chargeActionMessages = {};
    const { value: selectedVehicleIdLocal } = await Preferences.get({
      key: 'selectedVehicleId'
    });
    this.vehicles$ = this._enodeService.getVehicles();
    this.vehicles$.subscribe({
      next: (vehicles) => {
        this.vehicles = vehicles;
        if (
          selectedVehicleIdLocal &&
          this.vehicles.find((v) => v.id === selectedVehicleIdLocal)
        ) {
          this.selectedVehicleId = selectedVehicleIdLocal;
        } else {
          this.selectedVehicleId = this.vehicles[0]?.id;
        }
        this.lastUpdated = new Date().toLocaleDateString('nb-NO', {
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        });
        this.isLoading = false;
      },
      error: (err) => {
        this.hasFetchingErrorVehicles = true;
      }
    });
  }

  finishSetup() {
    this.isLoading = true;
    this.doSetup = false;
    this.getMe();
  }

  locationsChanged(locations: UserLocation[]) {
    this.locations = locations;
  }

  async menuDismissed(modalEvent: CustomEvent) {
    this.showMenu = false;
    const data = modalEvent.detail?.data;
    if (data?.locations) {
      this.locations = data.locations;
    }
    if (data?.selectedVehicleId) {
      this.selectedVehicleId = data.selectedVehicleId;
      await Preferences.set({
        key: 'selectedVehicleId',
        value: data.selectedVehicleId
      });
    }
  }

  async showMenuModal() {
    this.showMenu = true;
  }
}
